/**
 * WHICH RECORD THIS BROWSER IS LOOKING AT, read off the address bar.
 *
 * The org is the subdomain (docs/PLATFORMS.md: only ORG names one), so
 * the host answers a question nothing else can at load time -- before
 * any token exists, before any request goes out. `{org}.polywog.io` is
 * a record's platform; the bare apex is not a record at all.
 *
 * That distinction is what the apex screen needs. polywog.io cannot
 * sign anybody in: Identity Platform tenants are per-org user pools
 * (the same email in two orgs is two distinct users), so there is no
 * tenant to authenticate against until an org is named. The apex asks
 * which one and sends you there; the sign-in happens on the subdomain,
 * inside the tenant it selects.
 *
 * Label counting, not a configured base domain, on purpose: a record's
 * host has one more label than the apex, and that is true of
 * polywog.io, of a preview domain, and of whatever the fleet is called
 * next. A base domain in the bundle would be a fourth copy of a name
 * the naming law already owns.
 */
import { isOrgName } from '../../api';

/** Labels in the bare apex (`polywog.io`). A record adds one. */
const APEX_LABELS = 2;

/**
 * Hosts that are a developer's machine rather than a platform. They
 * have no subdomain and must never dispatch -- `make up` would bounce
 * the browser off the local stack and into the real fleet.
 */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '0.0.0.0']);

/**
 * Whether this host is a developer's machine rather than a platform.
 *
 * The third answer the host law can give, and the two above already
 * treat it as its own: not the apex (nothing to dispatch) and not a
 * record (no org, no tenant). The sign-in screen needs the distinction
 * spelled out because both of its exits -- start the trip, or return
 * to the apex -- name a front door a dev machine does not have.
 */
export function isLocalHost(hostname: string): boolean {
  return LOCAL_HOSTS.has(hostname);
}

/**
 * The org this host serves, or null when the host is not a record's.
 *
 * Null means one of three things, all of which want the same answer
 * from the caller (do not assume an org): the bare apex, a local dev
 * host, or a leading label whose SHAPE is not an org name's.
 *
 * Shape only. The reserved names (`www`, `api`, `app`...) are policy
 * the server owns, and core/platform/org.py keeps them a validator rather
 * than a constraint on purpose -- "this is policy, not shape; the list
 * grows without the published contract changing". So the list does not
 * publish, and a browser carrying its own copy would be a copy that
 * silently falls behind. `www.polywog.io` is a HOSTING concern: point
 * it at the apex site and this code never sees it.
 */
export function orgFromHost(hostname: string): string | null {
  if (isLocalHost(hostname)) return null;
  const labels = hostname.split('.');
  if (labels.length <= APEX_LABELS) return null;
  const candidate = labels[0];
  return isOrgName(candidate) ? candidate : null;
}

/**
 * Whether this browser is at the fleet's front door rather than inside
 * a record.
 */
export function isApexHost(hostname: string): boolean {
  return !isLocalHost(hostname) && hostname.split('.').length <= APEX_LABELS;
}

/**
 * Where naming an org sends you: the same host with the org in front.
 *
 * Built from the CURRENT host so the apex never needs to be told what
 * domain it is serving -- a preview deploy dispatches within itself
 * rather than throwing people at production.
 */
export function recordUrl(org: string, location: Location, email = '', next: string | null = null): string {
  // `next` is the add-in's return trip (PRD-553), already validated by
  // addinReturnPath: the front door carries it to the record untouched.
  const base = `${location.protocol}//${org}.${location.host}${next ?? ''}`;
  // THE ADDRESS RIDES ALONG, and it is what makes the record able to
  // sign somebody in without asking a second time. They typed it at the
  // front door; the record hands it to Google as the account to use, so
  // nobody is ever presented with a chooser.
  //
  // In the FRAGMENT, not a query: fragments are not sent to the server,
  // do not reach access logs, and are not carried on a Referer. This is
  // the person's own address in their own browser and it authorizes
  // nothing -- the seat is held for an identity only Google can attest
  // -- but there is no reason to write it into anybody's logs.
  return email ? `${base}#email=${encodeURIComponent(email)}` : base;
}

/**
 * The address the front door sent along, or null when it sent none.
 *
 * NO ADDRESS MEANS THEY DID NOT COME THROUGH THE FRONT DOOR. A record
 * signs in the person the apex identified; somebody who typed the
 * subdomain directly has told us nothing, and belongs at polywog.io.
 *
 * @param location - The browser's current location.
 * @returns The address, or null.
 */
export function invitedEmail(location: Location): string | null {
  const match = /(?:^|&)email=([^&]+)/.exec(location.hash.replace(/^#/, ''));
  return match ? decodeURIComponent(match[1]) : null;
}


/**
 * The org an email address belongs to, or null when it names none.
 *
 * AN ORG HOSTS ITS OWN EMAIL DOMAIN (user ruling): that is what makes
 * this derivable at all, and it is why the apex can ask for an address
 * instead of a workspace name. `you@reliant-eng.com` is reliant-eng.
 *
 * DERIVED, NEVER LOOKED UP. The whole computation happens in the
 * browser with no request, so there is nothing to enumerate -- an
 * unauthenticated endpoint answering "does this address have an
 * account, and at which firm" would hand over the customer list of a
 * product whose customers are named engineering firms. The domain
 * already carries the answer.
 *
 * The label taken is the one BEFORE the public suffix, so a mail
 * subdomain does not fool it (`you@mail.reliant-eng.com` is still
 * reliant-eng). A multi-part suffix would (`acme.co.uk` reads as
 * `co`); the address is choosing which subdomain to sign in on, and
 * the wrong one refuses at its own door.
 */
export function orgFromEmail(email: string): string | null {
  const at = email.lastIndexOf('@');
  if (at < 0) return null;
  const labels = email
    .slice(at + 1)
    .trim()
    .toLowerCase()
    .split('.')
    .filter(Boolean);
  if (labels.length < 2) return null;
  const candidate = labels[labels.length - 2];
  return isOrgName(candidate) ? candidate : null;
}

/**
 * The front door, from inside a record.
 *
 * A record's host is the apex with one label in front, so dropping
 * that label walks back out -- no configured base domain, for the same
 * reason orgFromHost counts labels rather than matching one.
 *
 * @param location - The browser's current location.
 * @returns The apex URL this subdomain sits under.
 */
export function apexUrl(location: Location): string {
  const labels = location.host.split('.');
  return `${location.protocol}//${labels.slice(1).join('.')}`;
}

/**
 * The path an invite link lands on at the apex: `/join/{org}`.
 *
 * The org rides the PATH rather than a query string or a token,
 * because it is not a secret and pretending otherwise would be
 * theatre. Knowing an org's name grants nothing -- the subdomain is
 * public, and the seat is held for a VERIFIED email that only the
 * identity provider can attest. The link's whole job is to save the
 * invited person from having to be told what to type.
 */
const JOIN_PREFIX = '/join/';

/**
 * The org an invite link names, or null when this is not one.
 *
 * Shape-checked like every other org read off the address bar: a
 * malformed path prefills nothing rather than sending somebody to a
 * subdomain that cannot exist.
 */
export function invitedOrgFromPath(pathname: string): string | null {
  if (!pathname.startsWith(JOIN_PREFIX)) return null;
  const candidate = pathname.slice(JOIN_PREFIX.length).replace(/\/+$/, '');
  return isOrgName(candidate) ? candidate : null;
}

/**
 * The link an operator sends an invited person: the apex, carrying the
 * org they were invited to.
 */
export function invitationUrl(org: string, apex: string): string {
  return `https://${apex}${JOIN_PREFIX}${org}`;
}

/**
 * THE REVIT ADD-IN'S SIGN-IN TRIP (PRD-553, ruled 2026-10-13).
 *
 * The add-in opens `/addin/callback?port=<p>&state=<s>` in the system
 * browser. The record signs the person in as it always does (MFA
 * included), then hands the ID token to the add-in's loopback listener.
 * The port and the state are the add-in's own; both are shape-checked
 * here because they steer where a token is sent: loopback only, an
 * unprivileged port, and an opaque state the listener compares.
 */
export const ADDIN_CALLBACK_PATH = '/addin/callback';

/** What the add-in sent: where its listener is, and its own nonce. */
export type AddinTrip = { port: number; state: string };

const MIN_PORT = 1024;
const MAX_PORT = 65535;
const STATE_SHAPE = /^[A-Za-z0-9_-]{16,128}$/;

/**
 * The trip a query string names, or null when it is not a well-formed one.
 *
 * @param search - The location's search string.
 * @returns The port and state, or null.
 */
export function parseAddinTrip(search: string): AddinTrip | null {
  const params = new URLSearchParams(search);
  const rawPort = params.get('port') ?? '';
  const state = params.get('state') ?? '';
  if (!/^[0-9]{1,5}$/.test(rawPort) || !STATE_SHAPE.test(state)) return null;
  const port = Number(rawPort);
  if (port < MIN_PORT || port > MAX_PORT) return null;
  return { port, state };
}

/**
 * Where the token goes: the add-in's loopback listener, in the FRAGMENT
 * so the token is never part of a request line (no log, no Referer).
 * The listener's page reads it and posts it to itself.
 */
export function loopbackCallbackUrl(trip: AddinTrip, idToken: string): string {
  const fragment = `id_token=${encodeURIComponent(idToken)}&state=${encodeURIComponent(trip.state)}`;
  return `http://127.0.0.1:${trip.port}/callback#${fragment}`;
}

/**
 * The path the front door must carry to the record to resume a trip,
 * when this location is one; null otherwise. Rebuilt from the parsed
 * values, never echoed from the address bar.
 */
export function addinReturnPath(location: Pick<Location, 'pathname' | 'search'>): string | null {
  if (location.pathname !== ADDIN_CALLBACK_PATH) return null;
  const trip = parseAddinTrip(location.search);
  return trip ? `${ADDIN_CALLBACK_PATH}?port=${trip.port}&state=${trip.state}` : null;
}

/**
 * Where a record sends somebody who did not come through the front
 * door: the apex, carrying the add-in trip (if any) as `next`.
 */
export function apexReturnUrl(location: Location): string {
  const next = addinReturnPath(location);
  return next ? `${apexUrl(location)}/?next=${encodeURIComponent(next)}` : apexUrl(location);
}

/** The `next` a record sent back to the apex, re-validated, or null. */
export function nextFromSearch(search: string): string | null {
  const next = new URLSearchParams(search).get('next') ?? '';
  const q = next.indexOf('?');
  if (q < 0) return null;
  return addinReturnPath({ pathname: next.slice(0, q), search: next.slice(q) });
}
